import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TableContainer,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faPrint, faSave } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { fetchDataWithRetries, Url } from "../../../Component/function/FunctionApi";
import { getCookie } from "../../../Pages/Login/Login";
import Swal from "sweetalert2";
import useShowInvoice from "../../../hooks/invoice/ShowInvoice";
import useExportExcel from "../../../hooks/ExportExcel/userExportExcel";

// Custom hook for debouncing input values
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const authHeaders = () => ({
  Authorization: `Bearer ${
    getCookie("user") ? JSON.parse(getCookie("user")).token : ""
  }`,
});

const DelayedInvoicesTable = () => {
  const { showInvoice } = useShowInvoice();
  const { handleExportToExcel } = useExportExcel();
  
  const [apiResult, setApiResult] = useState({
    data: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debouncedSearchTerms = useDebounce(searchTerms, 500);

  // Refs for search inputs (simplified)
  const invoiceCodeRef = useRef();
  const customerRef = useRef();
  const governorateRef = useRef();
  const representativeRef = useRef();
const searchTermsRef = useRef({});

 const fetchDelayedInvoices = async (pageNumber = 1, pageSize = 10, terms = null) => {

  setLoading(true);
    setError(null);
    
    const activeTerms = terms !== null ? terms : searchTerms;
    
    try {
      const response = await axios.get(`${Url}Invoice/postponed`, {
        headers: authHeaders(),
        params: {
          Page: pageNumber,
          PageSize: pageSize,
          ...(activeTerms.invoiceCode && { InvoiceCode: activeTerms.invoiceCode }),
          ...(activeTerms.customer && { Customer: activeTerms.customer }),
          ...(activeTerms.governorate && { Governorate: activeTerms.governorate }),
          ...(activeTerms.representative && { Representative: activeTerms.representative }),
        },
      });
       
      const payload = response.data || {};
      setApiResult({
        data: payload.data || [],
        totalCount: payload.totalCount || 0,
        totalPages: payload.totalPages || 0,
        currentPage: payload.currentPage || pageNumber, 
        pageSize: payload.pageSize || pageSize,      
        hasPreviousPage: Boolean(payload.hasPreviousPage),
        hasNextPage: Boolean(payload.hasNextPage),
      });
      setSelectedRows([]);
    } catch (e) {
      setError("خطأ في جلب البيانات من الخادم");
      console.error("Error fetching delayed invoices:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDelayedInvoices(1, apiResult.pageSize);
  }, []);

  // Refetch when search terms change (debounced)
useEffect(() => {
    fetchDelayedInvoices(1, apiResult.pageSize, debouncedSearchTerms);
}, [debouncedSearchTerms]);

  const handleSearch = (column, value) => {
    setSearchTerms((prev) => {
      const updated = { ...prev, [column]: value };
      searchTermsRef.current = updated; 
      return updated;
    });
};
  const handleClearSearch = () => {
    // Clear simplified search inputs
    const refs = {
      invoiceCodeRef,
      customerRef,
      governorateRef,
      representativeRef,
    };

    Object.values(refs).forEach(ref => {
      if (ref.current) ref.current.value = "";
    });

    setSearchTerms({});
  };

  const handleSelectAll = (event) => {
    setSelectedRows(
      event.target.checked ? (apiResult.data || []).map((row) => row.id) : []
    );
  };

  const handleRowSelect = (event, row) => {
    setSelectedRows((prev) =>
      event.target.checked
        ? [...prev, row.id]
        : prev.filter((id) => id !== row.id)
    );
  };

  const handlePrint = () => {
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "تحذير",
        text: "يرجى اختيار فواتير للطباعة",
      });
      return;
    }
    // TODO: Implement print functionality
    Swal.fire({
      icon: "info",
      title: "قريباً",
      text: "سيتم تنفيذ وظيفة الطباعة قريباً",
    });
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "تحذير", 
        text: "يرجى اختيار فواتير للتصدير",
      });
      return;
    }
    // TODO: Implement export functionality
    Swal.fire({
      icon: "info",
      title: "قريباً",
      text: "سيتم تنفيذ وظيفة التصدير قريباً",
    });
  };

  const handleRemoveDelay = async () => {
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "تحذير",
        text: "يرجى اختيار فواتير لإلغاء التأجيل",
      });
      return;
    }

    const result = await Swal.fire({
      title: "تأكيد إلغاء التأجيل",
      text: `هل أنت متأكد من إلغاء تأجيل ${selectedRows.length} فاتورة؟`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم، إلغاء التأجيل",
      cancelButtonText: "إلغاء",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      try {
        // Process each selected invoice
        const removeDelayPromises = selectedRows.map(async (invoiceId) => {
          const requestBody = {
            isPostponed: false,
            postponedUntil: null,
          };
          
          return await axios.put(`${Url}Invoice/postpone/${invoiceId}`, requestBody, {
            headers: authHeaders(),
          });
        });

        await Promise.all(removeDelayPromises);
        
        Swal.fire({
          title: "تم بنجاح!",
          text: "تم إلغاء تأجيل الفواتير المحددة",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        
        setSelectedRows([]);
        await fetchDelayedInvoices(apiResult.currentPage, apiResult.pageSize);
      } catch (error) {
        console.error("Error removing delay:", error);
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: error.response?.data?.message || "حدث خطأ أثناء إلغاء التأجيل",
        });
      }
    }
  };

  // Use API data directly (no client-side filtering needed)
  const currentRows = apiResult.data || [];

  const renderSearchInput = (column, placeholder, ref) => (
    <td key={column}>
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerms[column] || ""}
        onChange={(e) => handleSearch(column, e.target.value)}
        ref={ref}
        className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </td>
  );

  return (
    <div className="font-medium">
      <Paper>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            padding: "0 16px",
          }}
        >
          <div className="flex flex-wrap items-center gap-10">
            <Button
              onClick={() => handleExportToExcel(currentRows, selectedRows)}
            >
              <FontAwesomeIcon icon={faSave} /> تصدير إلى Excel
            </Button>
            {selectedRows.length > 0 && (
              <button
                className="px-4 my-3 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
                onClick={handleRemoveDelay}
              >
                إلغاء التأجيل
              </button>
            )}
            <button
              className="px-4 my-3 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              onClick={handleClearSearch}
            >
              مسح
            </button>
          </div>
          <h3 className="my-3 text-slate-700 font-medium">
            عدد الفواتير المحددة: {selectedRows.length}
          </h3>
          <h3 className="my-3 text-slate-700 font-medium">
            عدد الفواتير الكلي: {apiResult.totalCount}
          </h3>
        </div>
        <TableContainer>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* Search Inputs Row */}
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input type="checkbox" className="form-checkbox" />
                  </th>
                  {currentRows && currentRows.length > 0 && (
                    <>
                      {renderSearchInput("invoiceCode", "كود", invoiceCodeRef)}
                      {renderSearchInput("customer", "العميل", customerRef)}
                      {renderSearchInput("governorate", "المحافظة", governorateRef)}
                      {renderSearchInput("representative", "المندوب", representativeRef)}
                    </>
                  )}
                </tr>

                {/* Header Row */}
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={selectedRows.length === currentRows.length && currentRows.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    كود
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    التاريخ
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    العميل
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المحافظة
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الهاتف
                  </th>
                  <th className="px-12 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الفئات
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تاريخ التأجيل
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المندوب
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجمالي
                  </th>
                  <th className="p-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    عرض
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-4 text-center">
                      <CircularProgress />
                    </td>
                  </tr>
                ) : currentRows.length > 0 ? (
                  currentRows.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        selectedRows.includes(row.id)
                          ? "bg-blue-50"
                          : "hover:bg-gray-50"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={selectedRows.includes(row.id)}
                          onChange={(event) => handleRowSelect(event, row)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-bold">
                        {row.invoiceCode}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.invoiceDate.split("T")[0]}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.customer}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.governorate?.name}
                      </td>
                      <td className="p-1 flex flex-col gap-1">
                        <p>{row.phone}</p>
                        <p>{row?.socendPhone || null}</p>
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.invoiceCategories
                          ?.map((cat) => cat.category.name)
                          .join(", ")}
                      </td>
                      <td className="p-1 break-words whitespace-wrap text-red-600 font-medium">
                        {row.postponedUntil?.split("T")[0] || "لا يوجد تاريخ"}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.representative?.fullName ?? "لا يوجد مندوب"}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        {row.totalPrice}
                      </td>
                      <td className="p-1 break-words whitespace-wrap">
                        <button
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          onClick={() => showInvoice(row)}
                        >
                          عرض
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-center gap-4 py-6">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => apiResult.hasPreviousPage && fetchDelayedInvoices(apiResult.currentPage - 1, apiResult.pageSize)}

            disabled={!apiResult.hasPreviousPage}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2
              ${
                apiResult.currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
              }`}
          >
            السابق
          </button>

          <div className="flex items-center gap-1">
            {Array.from(
              { length: Math.max(1, apiResult.totalPages) },
              (_, index) => {
                const pageNumber = index + 1;
                const isNearCurrentPage =
                  Math.abs(pageNumber - apiResult.currentPage) <= 1;
                const isFirstPage = pageNumber === 1;
                const isLastPage = pageNumber === apiResult.totalPages;

                if (isFirstPage || isLastPage || isNearCurrentPage) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() =>
                        fetchDelayedInvoices(pageNumber, apiResult.pageSize)
                      }
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
                      ${
                        apiResult.currentPage === pageNumber
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  (pageNumber === 2 && apiResult.currentPage > 3) ||
                  (pageNumber === apiResult.totalPages - 1 &&
                    apiResult.currentPage < apiResult.totalPages - 2)
                ) {
                  return (
                    <span key={pageNumber} className="px-2">
                      ...
                    </span>
                  );
                }
                return null;
              }
            )}
          </div>

          <button
            onClick={() => apiResult.hasNextPage && fetchDelayedInvoices(apiResult.currentPage + 1, apiResult.pageSize)}

            disabled={!apiResult.hasNextPage}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2
              ${
                apiResult.currentPage === apiResult.totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
              }`}
          >
            التالي
          </button>
        </div>

        <div className="text-sm text-gray-600">
          صفحة {apiResult.currentPage} من {Math.max(1, apiResult.totalPages)}
        </div>
      </div>
    </div>
  );
};

export default DelayedInvoicesTable;
